import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, MessageCircle, Clock, MapPin, Zap, CalendarClock, Wrench, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { COMPANY, EMERGENCY, EQUIPMENT_OPTIONS, BREAKDOWN_OPTIONS, waLink } from "../data/site";
import { api, apiError } from "../lib/api";
import { Reveal, Overline } from "../components/Reveal";

const inputCls =
  "w-full py-3.5 px-4 bg-surface border border-border rounded-xl text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand transition-all";

const EMPTY = { name: "", phone: "", email: "", city: "", equipment: "", issue: "", weekend: false, message: "" };

export default function Emergency() {
  const waMsg = "URGENCE — Bonjour MS Énergie, j'ai une panne et besoin d'une intervention rapide.";
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.equipment) return toast.error("Sélectionnez l'équipement concerné.");
    if (!form.issue) return toast.error("Sélectionnez le type de panne.");
    setLoading(true);
    try {
      await api.post("/depannage", form);
      setDone(true);
      toast.success("Demande de dépannage envoyée ! Nous vous rappelons rapidement.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="emergency-page">
      <section className="bg-brand text-white relative overflow-hidden grain">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-white/10 rounded-full blur-[100px]" />
        <div className="max-w-7xl mx-auto px-5 md:px-10 pt-24 pb-20 relative">
          <div className="inline-flex items-center gap-2 bg-white/15 px-4 py-2 rounded-full text-sm font-bold">
            <Zap className="w-4 h-4" /> Service de dépannage 7j/7
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter mt-6 max-w-3xl leading-[1.02]">
            Dépannage d'urgence chauffage & climatisation
          </h1>
          <p className="text-white/90 text-lg mt-6 max-w-2xl">
            Une panne ne prévient jamais. Nos techniciens interviennent rapidement à {COMPANY.area} pour rétablir votre confort au plus vite.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-9">
            <a href={`tel:${COMPANY.phoneRaw}`} data-testid="emergency-page-call" className="bg-white text-brand font-extrabold py-4 px-8 rounded-full flex items-center justify-center gap-2 hover:bg-navy hover:text-white transition-colors">
              <Phone className="w-5 h-5" /> Appeler maintenant
            </a>
            <a href={waLink(waMsg)} target="_blank" rel="noopener noreferrer" data-testid="emergency-page-whatsapp" className="bg-[#25D366] text-white font-bold py-4 px-8 rounded-full flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
              <MessageCircle className="w-5 h-5" /> WhatsApp
            </a>
            <a href="#formulaire-depannage" data-testid="emergency-form-anchor" className="bg-navy text-white font-bold py-4 px-8 rounded-full flex items-center justify-center gap-2 hover:bg-navy-light transition-colors">
              <Wrench className="w-5 h-5" /> Décrire ma panne
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <Overline>Nous intervenons pour</Overline>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-navy mt-4">Les pannes les plus fréquentes</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {EMERGENCY.map((e, i) => (
            <Reveal key={e.title} delay={i * 0.08}>
              <div className="bg-white border border-border rounded-2xl p-7 h-full hover:border-brand hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center">
                  <e.icon className="w-6 h-6 text-brand" />
                </div>
                <h3 className="font-display font-bold text-lg text-navy mt-5">{e.title}</h3>
                <p className="text-text-muted text-sm mt-2 leading-relaxed">{e.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-surface border-y border-border py-20">
        <div className="max-w-5xl mx-auto px-5 md:px-10 grid sm:grid-cols-3 gap-8 text-center">
          {[
            { icon: Clock, t: "Réactivité", d: "Prise en charge rapide de votre demande." },
            { icon: MapPin, t: "Proximité", d: "Basés à Trélazé, au cœur du Maine-et-Loire." },
            { icon: Zap, t: "Efficacité", d: "Diagnostic précis et réparation durable." },
          ].map((b, i) => (
            <Reveal key={b.t} delay={i * 0.08}>
              <b.icon className="w-9 h-9 text-brand mx-auto" />
              <h3 className="font-display font-bold text-navy text-lg mt-4">{b.t}</h3>
              <p className="text-text-muted text-sm mt-2">{b.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WEEKEND URGENCY */}
      <section className="bg-navy text-white relative grain overflow-hidden">
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-brand/20 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 relative grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 bg-brand/20 text-brand px-4 py-2 rounded-full text-sm font-bold">
              <CalendarClock className="w-4 h-4" /> Urgence week-end & jours fériés
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-5">
              Une panne le samedi ou le dimanche ?
            </h2>
            <p className="text-white/60 mt-5 leading-relaxed">
              Le froid, la chaleur ou une fuite n'attendent pas le lundi. MS Énergie assure un service d'urgence le week-end et les jours fériés pour les pannes critiques de chauffage, climatisation et pompe à chaleur dans le {COMPANY.region}.
            </p>
            <ul className="space-y-3 mt-6">
              {["Astreinte week-end & jours fériés", "Intervention prioritaire sur les pannes critiques", "Diagnostic rapide et devis transparent"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-white/80">
                  <CheckCircle2 className="w-5 h-5 text-brand shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <a href={`tel:${COMPANY.phoneRaw}`} className="bg-brand hover:bg-brand-dark text-white font-bold py-4 px-8 rounded-full flex items-center justify-center gap-2 transition-colors">
                <Phone className="w-5 h-5" /> Urgence week-end
              </a>
              <a href={waLink(waMsg)} target="_blank" rel="noopener noreferrer" className="border-2 border-white/20 hover:border-brand text-white font-bold py-4 px-8 rounded-full flex items-center justify-center gap-2 transition-colors">
                <MessageCircle className="w-5 h-5" /> WhatsApp
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-display font-bold text-xl">Bon à savoir</p>
                  <p className="text-white/60 text-sm mt-2 leading-relaxed">
                    En cas de fuite de gaz ou de danger immédiat, coupez l'alimentation et contactez les services d'urgence. Pour toute autre panne, utilisez le formulaire ci-dessous : précisez l'équipement et le type de panne pour une prise en charge plus rapide.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* DEPANNAGE FORM */}
      <section className="max-w-4xl mx-auto px-5 md:px-10 py-24" id="formulaire-depannage">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Overline>Demande de dépannage</Overline>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-navy mt-4">Décrivez votre panne en 1 minute</h2>
          <p className="text-text-muted mt-4">Sélectionnez l'équipement et le type de panne, nous vous rappelons rapidement.</p>
        </div>

        <Reveal>
          <div className="bg-white border border-border rounded-3xl p-8 md:p-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            {done ? (
              <div className="text-center py-12" data-testid="depannage-success">
                <div className="w-16 h-16 bg-brand/10 rounded-full flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9 text-brand" /></div>
                <h3 className="font-display text-2xl font-extrabold text-navy mt-6">Demande envoyée !</h3>
                <p className="text-text-muted mt-3">Notre équipe vous rappelle au plus vite pour votre dépannage.</p>
                <button onClick={() => setDone(false)} className="mt-7 bg-navy text-white font-bold py-3 px-7 rounded-full hover:bg-navy-light transition-colors">
                  Nouvelle demande
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="depannage-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="flex items-center gap-2 text-sm font-semibold text-navy mb-1.5"><Wrench className="w-4 h-4 text-brand" /> Équipement concerné *</span>
                    <select required value={form.equipment} onChange={set("equipment")} data-testid="depannage-equipment" className={inputCls}>
                      <option value="">Sélectionnez…</option>
                      {EQUIPMENT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="flex items-center gap-2 text-sm font-semibold text-navy mb-1.5"><AlertTriangle className="w-4 h-4 text-brand" /> Type de panne *</span>
                    <select required value={form.issue} onChange={set("issue")} data-testid="depannage-issue" className={inputCls}>
                      <option value="">Sélectionnez…</option>
                      {BREAKDOWN_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </label>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <input required value={form.name} onChange={set("name")} data-testid="depannage-name" className={inputCls} placeholder="Nom complet *" />
                  <input required value={form.phone} onChange={set("phone")} data-testid="depannage-phone" className={inputCls} placeholder="Téléphone *" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <input type="email" value={form.email} onChange={set("email")} data-testid="depannage-email" className={inputCls} placeholder="Email" />
                  <input value={form.city} onChange={set("city")} data-testid="depannage-city" className={inputCls} placeholder="Ville" />
                </div>
                <textarea rows={4} value={form.message} onChange={set("message")} data-testid="depannage-message" className={`${inputCls} resize-none`} placeholder="Précisions sur la panne (code erreur, depuis quand…)" />
                <label className="flex items-center gap-3 text-sm font-medium text-navy cursor-pointer">
                  <input type="checkbox" checked={form.weekend} onChange={(e) => setForm((f) => ({ ...f, weekend: e.target.checked }))} data-testid="depannage-weekend" className="w-5 h-5 accent-[#2FA32A]" />
                  Intervention souhaitée le week-end / jour férié
                </label>
                <button type="submit" disabled={loading} data-testid="depannage-submit" className="w-full bg-brand hover:bg-brand-dark text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                  {loading ? "Envoi…" : <>Envoyer ma demande de dépannage <Send className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 text-center">
        <h2 className="font-display text-2xl md:text-3xl font-extrabold text-navy">Pas une urgence ? Planifiez votre intervention</h2>
        <Link to="/rendez-vous" className="inline-block mt-6 bg-navy text-white font-bold py-4 px-8 rounded-full hover:bg-navy-light transition-colors">
          Prendre rendez-vous en ligne
        </Link>
      </section>
    </div>
  );
}
